// Is this module LOCKED for the selected business: enforced by the api and not
// held (N3, design_n3_module_enforcement.md)?
//
// useBusinessModule answers "held", which is a different question: a module
// the api does not enforce is open to every business, held or not, and a
// screen that treats "not held" as "locked" refuses what the api would accept.
// So the lock needs both halves, and reads the enforced half from the api
// (`enforced_modules`, the catalogue's flag) rather than keeping a copy here,
// which keeps switching a module on a one-line api change.
//
// FAIL-OPEN ON DISPLAY ONLY. An api that predates `enforced_modules` locks
// nothing here; the api's 403 `module_required` is the gate either way, and a
// banner on a module nobody enforces would be false.

import { useBusinessOptional } from '../contexts/BusinessContext';
import type { BusinessModuleKey } from '../types/businessApi';

export function useModuleLock(key: BusinessModuleKey): boolean {
  const current = useBusinessOptional()?.currentBusiness;
  if (!current) return false;
  const enforced = current.enforced_modules?.includes(key) === true;
  return enforced && current.modules?.[key] !== true;
}

/**
 * May the selected business WRITE to this module's own settings (late fees,
 * accounting basis)? Q-A (Dave, 2026-10-09): "lock when enforcement is turned
 * on" — so not while the module is locked (enforced and not held), and not
 * while the business itself is read-only. Holding the module is NOT required
 * while it is unenforced: the api accepts the write, and with no grandfather
 * left a landlord who has not paid yet would otherwise lose settings it uses.
 * False outside a business (preview), as useBusinessModule's canMutate was.
 */
export function useModuleWritable(key: BusinessModuleKey): boolean {
  const business = useBusinessOptional();
  const locked = useModuleLock(key);
  if (!business?.currentBusiness) return false;
  return !locked && business.readOnly !== true;
}
